<template>
  <div class="container p-0" v-show="!hidden">
    <div class="input-group has-validation">
      <!-- Dit veld had als enige geen id én geen name: het label kon er dus
           sowieso niet aan gekoppeld worden, en bij validatie was het veld niet
           te identificeren om de focus naartoe te verplaatsen. -->
      <select ref="selectField" :id="ids.input" :name="element.name" v-model="formData" @change="elementChanged"
        class="form-select" :required="element.required" :multiple="element.multiple"
        :aria-describedby="describedBy" :aria-invalid="ariaInvalid" :aria-busy="isLoading ? 'true' : undefined"
        :disabled="hidden">
        <option v-if="!element.multiple" value="" selected>{{ getTextFor(element.placeholder, lang) }}</option>
        <option v-for="option in selectOptions" :value="option.value" :key="option.value">{{ getTextFor(option.text, lang) }}</option>
      </select>
      <div v-if="isLoading" class="input-group-append">
        <span class="input-group-text">
          <div class="spinner-border spinner-border-sm" role="status">
            <span class="visually-hidden">Laden...</span>
          </div>
        </span>
      </div>
    </div>
    <div class="invalid-feedback" :id="ids.error" :style="invalidStyle">{{ getTextFor(element.invalid, lang) }}</div>
    <div v-if="heeftUitleg" :id="ids.help" class="form-text">{{ getTextFor(element.explanation, lang) }}</div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import FormSubmitService from '../../services/FormSubmitService';
import veldToegankelijkheid from '../../utils/veldToegankelijkheid';

export default defineComponent({
  mixins: [veldToegankelijkheid],
  name: 'FormElementSelect',
  props: ['element', 'lang', 'targetId', 'wasValidated', 'hidden'],
  
  data() {
    return {
      formData: "",
      selectOptions: [] as Array<{text: {[key: string]: string}, value: string}>,
      isLoading: false,
      formSubmitService: new FormSubmitService()
    }
  },
  
  watch: {
    'element.relationConfig': {
      handler() {
        this.loadOptions();
      },
      immediate: true
    }
  },
  
  methods: {
    elementChanged() {
      let processedValue = this.formData;
      
      // Behandel booleaanse en numerieke waarden voor enkele selectie
      if (!this.element.multiple) {
        if (processedValue === 'true') {
          processedValue = true;
        } else if (processedValue === 'false') {
          processedValue = false;
        } else if (!isNaN(Number(processedValue)) && processedValue !== '') {
          // Converteer numerieke strings naar echte getallen
          processedValue = Number(processedValue);
        }
      } 
      // Voor multiple select, converteer alle waarden in de array
      else if (Array.isArray(processedValue)) {
        processedValue = processedValue.map(value => {
          if (value === 'true') return true;
          if (value === 'false') return false;
          if (!isNaN(Number(value)) && value !== '') return Number(value);
          return value;
        });
      }
      
      this.$emit('elementChanged', this.element.name, processedValue);
    },
    
    checkFieldValidity(fieldRef: string) {
      const field = this.$refs[fieldRef];
      return field && field.checkValidity();
    },
    
    getTextFor(data:any, lang:string  ) {
      return data ? data[lang] : '';
    },
    
    async loadOptions() {
      if (this.element.relationConfig && this.element.relationConfig.enabled) {
        this.isLoading = true;
        try {
          const { collectionPath, labelField, valueField, formConfigPath } = this.element.relationConfig;
          const relatedRecords = await this.formSubmitService.getRelatedRecords(
            collectionPath,
            labelField,
            valueField || 'id',
            formConfigPath
          );
          
          this.selectOptions = relatedRecords;
        } catch (error) {
          console.error('Fout bij het ophalen van gerelateerde records:', error);
          this.selectOptions = this.element.options || [];
        } finally {
          this.isLoading = false;
        }
      } else {
        this.selectOptions = this.element.options || [];
      }
    }
  },
  
  created() {
    this.loadOptions();
  }
});
</script>

<style scoped>
.input-group-text {
  background-color: #f8f9fa;
}

/* Styling voor multiple select */
select[multiple] {
  height: auto;
  min-height: 100px;
}
</style>